import { useEffect, useState } from "react";

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { HttpMethodProps } from "../types";

interface RequestOptions {
  method: HttpMethodProps;
  route: string;
  body?: any;
}
function usePerformRequest<Type>({ method, route, body }: RequestOptions) {
  const [data, setData] = useState<Type>();
  const [response, setResponse] = useState<AxiosResponse>();
  const [error, setError] = useState<AxiosError>();
  const [status, setStatus] = useState<number | string>("undefined");
  const [isLoading, setLoading] = useState<boolean>(false);
  const reloadData = async () => {
    const token = localStorage.getItem("t");
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      method,
      data: body,
      url: route,
    };
    setLoading(true);
    const r: any = await axios.request(config).catch((e: AxiosError) => {
      setError(e);
      setStatus(e.response?.status as number);

      return {
        status: e.response?.status,
      };
    });
    setResponse(r as AxiosResponse);
    setStatus(r.status);
    setLoading(false);
    setData(r?.data ?? undefined);
  };
  useEffect(() => {
    reloadData().catch(() => setLoading(false));
  }, [route]);

  return { data, response, isLoading, reloadData, status, error };
}

interface PerformRequestOptions {
  method: "POST" | "PATCH" | "GET" | "PUT" | "DELETE";
  data?: any;
  route: string;
  headers?: any;
  doNotUseAuthorization?: boolean;
  onError?: () => any;
}

async function PerformRequest<Type>({
  method,
  data,
  route,
  headers,
  doNotUseAuthorization,
  onError,
}: PerformRequestOptions) {
  const token = localStorage.getItem("t");

  const requestHeaders = doNotUseAuthorization
    ? { ...headers }
    : { Authorization: `Bearer ${token}`, ...headers };
  
  // If data is FormData, don't set Content-Type header (let browser set it with boundary)
  const isFormData = data instanceof FormData;
  const finalHeaders = isFormData
    ? requestHeaders
    : { ...requestHeaders, "Content-Type": "application/json" };
  
  const config = {
    method,
    data,
    url: route,
    headers: finalHeaders,
  };

  const r: any = await axios.request(config).catch((e: AxiosError) => {
    if (onError) onError();
    return {
      status: e.response?.status,
      message: (e?.response?.data as any)?.message,
      data: (e?.response?.data as any)?.data,
    };
  });
  return {
    status: r.status as number,
    data: r?.data as Type,
    message: r?.message ?? undefined,
  };
}

export { usePerformRequest, PerformRequest };
